import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  AccountBalance as BankIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';

interface ABOCardProps {
  aboFile: any;
  onView: (id: number) => void;
  onDownload: (id: number) => void;
  onSendToBank?: (id: number) => void;
}

const statusColors = {
  generated: 'default',
  sent: 'success',
  sent_to_fio: 'primary',
  error: 'error',
} as const;

const statusLabels = {
  generated: 'Vytvořeno',
  sent: 'Odesláno',
  sent_to_fio: 'Odesláno do FIO',
  error: 'Chyba',
} as const;

const ABOCard: React.FC<ABOCardProps> = ({
  aboFile,
  onView,
  onDownload,
  onSendToBank,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const theme = useTheme();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  const getBankIcon = (bankType: string) => {
    switch (bankType.toLowerCase()) {
      case 'fio':
        return '🏦';
      case 'kb':
        return '🏛️';
      default:
        return '🏢';
    }
  };

  return (
    <Card
      sx={{
        mb: 2,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        '&:hover': {
          boxShadow: '0px 10px 15px rgba(0, 0, 0, 0.1), 0px 4px 6px rgba(0, 0, 0, 0.05)',
          transform: 'translateY(-2px)',
        },
      }}
      onClick={() => onView(aboFile.id)}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: 'text.primary',
                  mr: 2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
              >
                {aboFile.fileName || aboFile.filename}
              </Typography>
              <Chip
                label={statusLabels[aboFile.status as keyof typeof statusLabels] || aboFile.status}
                color={statusColors[aboFile.status as keyof typeof statusColors] || 'default'}
                size="small"
                sx={{ fontSize: '0.7rem' }}
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" sx={{ fontSize: '1.2rem' }}>
                  {getBankIcon(aboFile.bankType)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {aboFile.bankType.toUpperCase()}
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary">
                {aboFile.transactionCount || aboFile.transaction_count} transakcí
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {aboFile.totalAmount.toLocaleString('cs-CZ', { 
                  style: 'currency', 
                  currency: 'CZK' 
                })}
              </Typography>
              
              <Typography variant="caption" color="text.secondary">
                {format(new Date(aboFile.createdAt || aboFile.created_at), 'dd.MM.yyyy HH:mm', { locale: cs })}
              </Typography>
            </Box>
          </Box>
          
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleMenuOpen(e);
            }}
            sx={{ ml: 1 }}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
        
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={() => handleMenuAction(() => onView(aboFile.id))}>
            <ListItemIcon>
              <ViewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Detail</ListItemText>
          </MenuItem>
          
          <MenuItem onClick={() => handleMenuAction(() => onDownload(aboFile.id))}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Stáhnout</ListItemText>
          </MenuItem>
          
          {aboFile.status === 'generated' && onSendToBank && (
            <MenuItem onClick={() => handleMenuAction(() => onSendToBank(aboFile.id))}>
              <ListItemIcon>
                <SendIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText sx={{ color: 'primary.main' }}>Odeslat do banky</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </CardContent>
    </Card>
  );
};

export default ABOCard;
